import { HttpError, useOne, useParse, useUpdate } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import { FieldObjectType, IAttendantTemplateInputItem,  ISportParticipantDetail, ISportParticipantForm, SupportedLanguage } from "interfaces";
import { EditEwaiverInformationSection, EditInformationSection, EditRegistrationInformationSection } from "./components";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "components/button";
import { Link, useNavigate } from "react-router-dom";
import { get } from "lodash";
import toast from "react-hot-toast";
import { ParticipantFormSchema } from "components/attendantTemplate/schema";

const InformationSchema = z.object({
  bib: z.string().optional(),
  attendant_template_id: z.string().min(1, "This field is required"),
  is_active: z.boolean(),
  online_checked: z.boolean(),
  waiver_sign_completed: z.boolean(),
  kyc_by_legal: z.boolean(),
  kyc_completed: z.boolean(),
  ocr_time: z.number({
    invalid_type_error: "Ocr time must be a number",
    required_error: "Ocr time is required",
  })
    .min(0, {message:"Ocr time must be equal or greater than 0"})
    .nonnegative({ message: "Ocr time must be a positive number" }),
  face_matching_time: z.number({
    invalid_type_error: "Face matching time must be a number",
    required_error: "Face matching time is required",
  })
    .min(0, {message:"Face matching time must be equal or greater than 0"})
    .nonnegative({ message: "Face matching time must be a positive number" }),
  name_pick: z.string().optional(),
  email_pick: z.string().optional(),
  phone_pick: z.string().optional(),
  id_card_pick: z.string().optional(),
  num_of_times_update_authorized_info: z.number({
    invalid_type_error: "Max number of times to update authorized info must be a number",
    required_error: "Max number of times to update authorized info is required",
  })
    .min(0, {message:"Max number of times to update authorized info must be equal or greater than 0"})
    .nonnegative({ message: "Max number of times to update authorized info must be a positive number" }),
  num_of_times_change_pickup: z.number({
    invalid_type_error: "Max number of times to update pickup method must be a number",
    required_error: "Max number of times to update pickup method is required",
  })
    .min(0, {message:"Max number of times to update pickup method must be equal or greater than 0"})
    .nonnegative({ message: "Max number of times to update pickup method must be a positive number" }),
})

const CategorySchema = (languages: SupportedLanguage[]) => {
  const schemaFields = Object.fromEntries(
    languages.map((lang: SupportedLanguage) => [
      `category_name_${lang}`,
      z.string().min(1, "This field is required")
    ])
  );
  return z.object({
    category: z.object(schemaFields)
  });
}

export const EditSportParticipant: React.FC = () => {
  const parse = useParse();
  const { params } = parse();
  const {
    data,
    isFetching,
  } = useOne<ISportParticipantDetail>({
    resource: "participant",
    id: params?.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!params?.id,
    } as any,
  });

  const { data: groupData, isFetching: isFetchingGroups } = useOne<any[], HttpError>({
    resource: "content/dropdown/clubs?dict_response=true",
    id: "",
    meta: {
      resource: "data"
    },
  });

  const participantDetailData = data?.data ?? null;
  const groups = groupData?.data ?? null;

  if (isFetching || isFetchingGroups) {
    return <FullScreenLoading />;
  }
  return participantDetailData ? <EditSportParticipantForm participantDetailData={participantDetailData} groups={groups} /> : <></>;
}

const EditSportParticipantForm = ({participantDetailData, groups}: {participantDetailData: ISportParticipantDetail, groups: any}) => {
  const { mutate, isLoading: isUpdating } = useUpdate();
  const navigate = useNavigate();

  const methods = useForm<ISportParticipantForm, HttpError, ISportParticipantForm>({
    defaultValues: {
      attendant_template_id: participantDetailData?.participant?.attendant_template_id ?? "",
      is_active: participantDetailData.participant?.is_active ?? false,
      category: Object.fromEntries(
        (get(participantDetailData, "event.languages", []) || []).map(lang => [`category_name_${lang}`, get(participantDetailData.participant, `category_name_${lang}`, "") || ""])
      ),
      bib: participantDetailData.participant?.bib ?? "",
      online_checked: participantDetailData?.participant?.online_checked ?? false,
      waiver_sign_completed: participantDetailData?.participant?.waiver_sign_completed ?? false,
      kyc_by_legal: participantDetailData?.participant?.kyc_by_legal ?? false,
      kyc_completed: participantDetailData?.participant?.kyc_completed ?? false,
      ocr_time: participantDetailData?.participant?.ocr_time ?? 0,
      face_matching_time: participantDetailData?.participant?.face_matching_time ?? 0,
      name_pick: participantDetailData?.participant?.name_pick ?? "",
      email_pick: participantDetailData?.participant?.email_pick ?? "",
      phone_pick: participantDetailData?.participant?.phone_pick ?? "",
      id_card_pick: participantDetailData?.participant?.id_card_pick ?? "",
      num_of_times_update_authorized_info: participantDetailData?.participant?.num_of_times_update_authorized_info ?? 0,
      num_of_times_change_pickup: participantDetailData?.participant?.num_of_times_change_pickup ?? 0,
    },
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver: any = zodResolver(
        z.object({
          ...InformationSchema.shape,
          ...CategorySchema(get(participantDetailData, "event.languages", [])).shape,
          ...ParticipantFormSchema(attendantTemplate, get(values, "dob", null), get(participantDetailData, "event.start_date", "") || "").shape
        })
      );
      return createResolver(values, context, options);
    },
  });
  
  const { data: templateData, isLoading: isFetchingTemplate } = useOne<any[], HttpError>({
    resource: `participant/events/${participantDetailData?.event?.event_id}/attendant-templates/${methods.watch("attendant_template_id")}`,
    id: "",
    meta: {
      resource: "data"
    },
    queryOptions: {
      enabled: !!(methods.watch("attendant_template_id") && participantDetailData?.event?.event_id),
      select: (response: any) => {
        const formattedData: any = {};

        for (let groupKey in response.data) {
          const group = response.data[groupKey];
          formattedData[groupKey] = {
            ...group,
            fields: group.fields.map((field: IAttendantTemplateInputItem) => 
              field.object === FieldObjectType.CLUBS
                ? { ...field, option: groups }
                : field
            )
          };
        }

        return {
          ...response,
          data: {
            ...formattedData,
          },
        };
      },
    } as any
  });

  const attendantTemplate = templateData?.data ?? {};

  const onSubmit = (data: ISportParticipantForm) => {
    console.log(data);
    const {category, ...rest} = data;
    const submitValue = {...rest, ...category};
    mutate(
      {
        resource: "participant",
        id: participantDetailData.participant.participant_id,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValue,
        },
      },
      {
        onSuccess: () => {
          toast.success("Edit successfully");
          navigate(`/participants/sport/${participantDetailData.participant.participant_id}`);
        },
      }
    );
  }

  const onError = (errors: any) => {
    console.log(errors);
  };

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit, onError)} className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4 h-full">
          <Breadcrumbs customPageLabel="Edit Sport participant" />
          {participantDetailData ? (
            <>
              <EditInformationSection participantDetailData={participantDetailData} />
              <EditRegistrationInformationSection participantDetailData={participantDetailData} template={attendantTemplate} />
              <EditEwaiverInformationSection />
            </>
          ) : <></>}

          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto" style={{boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px"}}>
            <div className="flex items-center justify-end gap-x-4">
              <Link to={`/participants/sport/${participantDetailData?.participant.participant_id}`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button className="w-20" type="submit">
                Save
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
      {isFetchingTemplate || isUpdating ? <FullScreenLoading /> : <></>}
    </div>
  );
}